import { css } from '@emotion/core';
import { a2bTheme } from '@olxeu-a2b/ui/src/theme/styled';
import { SheetBottom } from '@nexus/lib-react/dist/core/SheetBottom/SheetBottom';
import { Modal } from '@nexus/lib-react/dist/core/Modal/Modal';
import {
  Headline3,
  Headline5,
  Paragraph3,
  Paragraph4,
  Paragraph8,
} from '@nexus/lib-react/dist/core/Typography';
import { LinkParagraph } from '@nexus/lib-react/dist/core/Link/LinkParagraph';
import styled from '../../../theme/styled';
import SafeDealIcon from '../../assets/protection_package.svg';
import { mq } from '../../styles';

type StyledTheme = Partial<a2bTheme>;

const flexBoxCol = () => css`
  display: flex;
  flex-direction: column;
`;

const smallMobileFontStyle = ({ theme }: { theme: StyledTheme }) => css`
  font-size: ${theme.sizes[12]};
  line-height: ${theme.space[14]};
`;

const mobileAndPhabletFontStyle = ({ theme }: { theme: StyledTheme }) => css`
  font-size: ${theme.sizes[12]};
  line-height: ${theme.space[16]};
`;

const SheetBottomModal = styled(SheetBottom)`
  ${mq.mobileAndPhabletHorizontal} {
    overflow: auto;
    height: 100vh;
    top: 0;
  }
`;

const InfoModal = styled(Modal)`
  ${({ theme }: { theme: StyledTheme }) => `
    z-index:${theme.zIndices.fixed};
  `}

  max-width: 552px;
  > div:first-of-type {
    padding-bottom: 0;
  }
`;

const ModalContent = styled.div`
  ${flexBoxCol};
  padding: 0 ${({ theme }) => theme.space[8]};

  ${mq.desktop} {
    justify-content: center;
    padding: 0 ${({ theme }) => theme.space[26]} ${({ theme }) => theme.space[26]};
  }
`;

const ModalImage = styled(SafeDealIcon)`
  margin: 0 auto;
  width: 60px;
  height: 60px;

  ${mq.smallMobile} {
    width: 40px;
    height: 40px;
  }

  ${mq.desktop} {
    width: 80px;
    height: 80px;
  }
`;

const ModalDesktopHeadline = styled(Headline3)`
  padding: ${({ theme }) => theme.space[10]} 0 ${({ theme }) => theme.space[24]};
  text-align: center;
`;

const ModalMobileHeadline = styled(Headline5)`
  padding: ${({ theme }) => theme.space[10]} 0 ${({ theme }) => theme.space[18]};
  text-align: center;
  ${mq.smallMobile} {
    font-size: ${({ theme }: { theme: StyledTheme }) => theme.fontSizes[16]};
  }
`;

const ModalBody = styled.div`
  ${flexBoxCol};
  justify-content: flex-start;
`;

const HelpCenterLink = styled(Paragraph3)`
  align-self: center;
  ${({ theme }) => css`
    font-weight: ${theme.fontWeights[400]};
    font-size: ${theme.fontSizes[12]};
    line-height: ${theme.lineHeights[14]};
    margin-top: ${theme.space[12]};

    ${mq.desktop} {
      font-size: ${theme.fontSizes[14]};
      line-height: ${theme.lineHeights[18]};
      margin-top: ${theme.space[16]};
    }

    > a {
      color: ${theme.colors['text-brand-primary']};
    }
  `}
`;

const Description = styled(Paragraph4)`
  text-align: center;
  padding-bottom: ${({ theme }: { theme: StyledTheme }) => theme.space[12]};
  ${mq.mobileAndPhablet} {
    ${mobileAndPhabletFontStyle}
  }

  ${mq.smallMobile} {
    ${smallMobileFontStyle};
    padding-bottom: ${({ theme }: { theme: StyledTheme }) => theme.space[8]};
  }

  ${mq.desktop} {
    text-align: left;
    padding-bottom: ${({ theme }: { theme: StyledTheme }) => theme.space[16]};
  }
`;

const AdditionalDescription = styled(Description)`
  text-align: left;
`;

const ListItem = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin: 0;
  font-size: ${({ theme }) => theme.sizes[14]};
  padding-bottom: ${({ theme }: { theme: StyledTheme }) => theme.space[8]};
  ${mq.mobileAndPhablet} {
    ${mobileAndPhabletFontStyle}
  }

  ${mq.smallMobile} {
    ${smallMobileFontStyle}
  }

  ${mq.desktop} {
    padding-bottom: ${({ theme }: { theme: StyledTheme }) => theme.space[12]};
  }

  p {
    margin: 0;
  }
`;

const ListItemDot = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  margin: 0 0 0 ${({ theme }) => theme.space[26]};
  font-size: ${({ theme }) => theme.sizes[14]};
  padding-bottom: ${({ theme }: { theme: StyledTheme }) => theme.space[8]};
  ${mq.mobileAndPhablet} {
    ${mobileAndPhabletFontStyle}
  }

  ${mq.smallMobile} {
    ${smallMobileFontStyle}
  }

  ${mq.desktop} {
    padding-bottom: ${({ theme }: { theme: StyledTheme }) => theme.space[12]};
    font-size: ${({ theme }) => theme.sizes[14]};
  }
`;

const MidDoT = styled.div`
  padding-right: ${({ theme }) => theme.space[12]};
`;

const AsteriskNote = styled(Paragraph8)`
  color: ${({ theme }) => theme.colors.grey5};
  margin-top: ${({ theme }) => theme.space[8]};

  ${mq.desktop} {
    margin-top: ${({ theme }) => theme.space[12]};
  }
`;

const Link = styled(LinkParagraph)`
  display: inline;
`;

export default {
  ListItem,
  ListItemDot,
  InfoModal,
  SheetBottomModal,
  ModalImage,
  ModalDesktopHeadline,
  ModalMobileHeadline,
  ModalContent,
  ModalBody,
  HelpCenterLink,
  Description,
  AsteriskNote,
  MidDoT,
  Link,
  AdditionalDescription,
};
